import { useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface CategoryRow {
  readonly categoryCode: string;
  readonly categoryName: string;
  readonly isLicenceCounted: boolean;
  /** null means no limit has been set, i.e. unlimited. */
  readonly licenceCount: number | null;
  readonly consumed: number;
  readonly remaining: number | null;
  readonly overLimit: boolean;
}

interface LicenceResponse {
  readonly tenantCode: string;
  readonly tenantName: string;
  readonly categories: CategoryRow[];
}

export function M19TenantLicencePage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const { code = '' } = useParams<{ code: string }>();

  const [data, setData] = useState<LicenceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  // Empty string is a real choice here: it means "no limit", so drafts cannot
  // be held as numbers.
  const [draft, setDraft] = useState<Record<string, string>>({});

  const load = (): void => {
    apiRequest<{ data: LicenceResponse }>(`/v1/platform/tenants/${code}/licence`, { token })
      .then((r) => {
        setData(r.data);
        setDraft(
          Object.fromEntries(
            r.data.categories.map((c) => [
              c.categoryCode,
              c.licenceCount === null ? '' : String(c.licenceCount),
            ]),
          ),
        );
      })
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token, code]);

  const save = async (categoryCode: string): Promise<void> => {
    setBusyCode(categoryCode);
    setError(null);
    setSaved(null);
    const raw = (draft[categoryCode] ?? '').trim();
    try {
      await apiRequest(`/v1/platform/tenants/${code}/licence`, {
        method: 'PUT',
        token,
        body: { categoryCode, licenceCount: raw === '' ? null : Number(raw) },
      });
      setSaved(categoryCode);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyCode(null);
    }
  };

  if (error && !data) return <p role="alert">{error}</p>;
  if (!data) return <p>{tr('m19.licence.loading')}</p>;

  return (
    <main aria-label="tenant-licence">
      <h1>{tr('m19.licence.title')}</h1>
      <p>
        {data.tenantName} ({data.tenantCode})
      </p>
      <p>{tr('m19.licence.help')}</p>
      <Link to="/admin/platform/tenants">{tr('m19.licence.back')}</Link>

      {error ? <p role="alert">{error}</p> : null}

      <DataTable<CategoryRow>
        ariaLabel="licence-table"
        rowKey={(c) => c.categoryCode}
        rows={data.categories}
        columns={[
          { key: 'category', header: tr('m19.licence.colCategory'), render: (c) => c.categoryName },
          {
            key: 'licences',
            header: tr('m19.licence.colLicences'),
            render: (c) =>
              c.isLicenceCounted ? (
                <input
                  type="number"
                  min={0}
                  aria-label={`licence-count-${c.categoryCode}`}
                  placeholder={tr('m19.licence.unlimited')}
                  value={draft[c.categoryCode] ?? ''}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, [c.categoryCode]: e.target.value }))
                  }
                />
              ) : (
                <span aria-label={`licence-exempt-${c.categoryCode}`}>
                  {tr('m19.licence.notCounted')}
                </span>
              ),
          },
          {
            key: 'consumed',
            header: tr('m19.licence.colConsumed'),
            cellAriaLabel: (c) => `licence-consumed-${c.categoryCode}`,
            render: (c) => String(c.consumed),
          },
          {
            key: 'remaining',
            header: tr('m19.licence.colRemaining'),
            cellAriaLabel: (c) => `licence-remaining-${c.categoryCode}`,
            render: (c) =>
              c.isLicenceCounted
                ? c.remaining === null
                  ? tr('m19.licence.unlimited')
                  : String(c.remaining)
                : '\u2014',
          },
          {
            key: 'actions',
            header: tr('m19.licence.colActions'),
            render: (c) => (
              <>
                {c.isLicenceCounted ? (
                  <Button
                    type="button"
                    variant="primary"
                    aria-label={`save-licence-${c.categoryCode}`}
                    disabled={busyCode === c.categoryCode}
                    onClick={() => void save(c.categoryCode)}
                  >
                    {busyCode === c.categoryCode
                      ? tr('m19.licence.saving')
                      : tr('m19.licence.save')}
                  </Button>
                ) : null}
                {c.overLimit ? (
                  <span aria-label={`licence-over-${c.categoryCode}`}>
                    {tr('m19.licence.overLimit')}
                  </span>
                ) : null}
                {saved === c.categoryCode ? (
                  <span role="status">{tr('m19.licence.saved')}</span>
                ) : null}
              </>
            ),
          },
        ]}
      />
    </main>
  );
}
